<template>
  <div class="delivery-address__container white-back-container">
    <div class="white-back-container__content">
      <div class="delivery-address__header-wrapper">
        <div class="delivery-address__header white-back-container__header">
          <div class="delivery-address__header-title">
            <h3 class="subhead-bold">お届け先の入力</h3>
            <p class="caption require-caption">必須*</p>
          </div>
          <div
            v-if="ticketUsableLimitAt"
            class="delivery-address__deadline-area"
          >
            <div class="delivery-address__deadline-date">
              <span class="delivery-address__deadline-label">
                住所入力期限：
              </span>
              <span class="delivery-address__deadline-value">
                {{ ticketUsableLimitAt }}
              </span>
              <span class="delivery-address__deadline-label">まで</span>
            </div>
            <p class="delivery-address__deadline-notice">
              期限を過ぎるとギフトの配送やご返金は承ることができません。
            </p>
          </div>
        </div>
      </div>
      <div
        class="delivery-address__input-container white-back-container__inner-content"
      >
        <div
          class="delivery-address-restriction box-list--attention align-left-box-list"
        >
          <div class="delivery-address-restriction__content">
            <div v-if="hasDeliveryRestriction">
              <div class="delivery-address-restriction__title-detail">
                <p class="delivery-address-restriction__title">
                  この商品には配送不可地域があります。
                </p>
                <p class="delivery-address-restriction__detail">
                  <TextWithLinks :text="deliveryRestrictionText" />
                </p>
                <p>
                  配送不可地域に該当する場合は
                  <a
                    href="https://giftee.com/contact/new"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="delivery-address-restriction__link"
                  >
                    こちらからお問い合わせください。
                  </a>
                </p>
              </div>
            </div>
            <div class="delivery-address-restriction__attention">
              <p class="delivery-address-restriction__title">
                以下の点にご注意ください。
              </p>
              <p>
                <span>・配送日のご指定は承っておりません。</span>
                <br />
                <span
                  >・配送は各ブランドより行われ、配送業者も異なるために営業所留めは対応しておりません。</span
                >
              </p>
            </div>
          </div>
        </div>
        <div class="name__container form-container">
          <div class="form-label body-bold">
            <span class="require">お名前</span>
          </div>
          <div class="form-input-group">
            <div v-if="showableNameErrorMessage" class="error-message">
              {{ nameErrorMessage }}
            </div>
            <div
              class="name-input__container form-input-container form-input-container-horizontal"
            >
              <input
                id="delivery_address_form_last_name"
                v-model="inputLastName"
                placeholder="姓"
                type="text"
                name="delivery_address_form[last_name]"
                class="input-form"
                :class="{
                  field_with_errors: isNotLastNameErrorMessageEmpty,
                  'input-form--inputted': isNotLastNameEmpty,
                  'input-form--inputting': isLastNameFocused
                }"
                @focus="onFocusLastName"
                @blur="onBlurLastName"
              />
              <input
                id="delivery_address_form_first_name"
                v-model="inputFirstName"
                placeholder="名"
                type="text"
                name="delivery_address_form[first_name]"
                class="input-form"
                :class="{
                  field_with_errors: isNotFirstNameErrorMessageEmpty,
                  'input-form--inputted': isNotFirstNameEmpty,
                  'input-form--inputting': isFirstNameFocused
                }"
                @focus="onFocusFirstName"
                @blur="onBlurFirstName"
              />
            </div>
            <div class="minimal form-minimal-info">
              ※宅配伝票に記載される宛名です
            </div>
          </div>
        </div>
        <div class="name-kana__container form-container">
          <div class="form-label body-bold">
            <span class="require">お名前（ふりがな）</span>
          </div>
          <div class="form-input-group">
            <div v-if="showableNameKanaErrorMessage" class="error-message">
              {{ nameKanaErrorMessage }}
            </div>
            <div
              class="name-kana-input__container form-input-container form-input-container-horizontal"
            >
              <input
                id="delivery_address_form_last_name_kana"
                v-model="inputLastNameKana"
                placeholder="せい"
                type="text"
                name="delivery_address_form[last_name_kana]"
                class="input-form"
                :class="{
                  field_with_errors: isNotLastNameKanaErrorMessageEmpty,
                  'input-form--inputted': isNotLastNameKanaEmpty,
                  'input-form--inputting': isLastNameKanaFocused
                }"
                @focus="onFocusLastNameKana"
                @blur="onBlurLastNameKana"
              />
              <input
                id="delivery_address_form_first_name_kana"
                v-model="inputFirstNameKana"
                placeholder="めい"
                type="text"
                name="delivery_address_form[first_name_kana]"
                class="input-form"
                :class="{
                  field_with_errors: isNotFirstNameKanaErrorMessageEmpty,
                  'input-form--inputted': isNotFirstNameKanaEmpty,
                  'input-form--inputting': isLastNameKanaFocused
                }"
                @focus="onFocusFirstNameKana"
                @blur="onBlurFirstNameKana"
              />
            </div>
          </div>
        </div>
        <div class="zip-code__container form-container">
          <div class="form-label body-bold">
            <span class="require">郵便番号</span>
          </div>
          <div class="minimal form-minimal-info">
            数字7ケタ、ハイフンなしでご入力ください
          </div>
          <div class="form-input-group">
            <div v-if="showableZipCodeErrorMessage" class="error-message">
              {{ zipCodeErrorMessage }}
            </div>
            <div class="zip-code-input__container form-input-container">
              <input
                id="delivery_address_form_zip_code"
                v-model="inputZipCode"
                placeholder="1234567"
                maxlength="7"
                size="7"
                type="text"
                name="delivery_address_form[zip_code]"
                class="input-form"
                :class="{
                  field_with_errors: showableZipCodeErrorMessage,
                  'input-form--inputted': isNotZipCodeEmpty,
                  'input-form--inputting': isZipCodeFocused
                }"
                @focus="onFocusZipCode"
                @blur="onBlurZipCode"
              />
            </div>
          </div>
        </div>
        <div class="address__container form-container">
          <div class="form-label body-bold">
            <span class="require">住所</span>
          </div>
          <div
            class="address-input__container form-input-container form-input-container-vertical"
          >
            <div class="form-input-group">
              <div v-if="showablePrefectureErrorMessage" class="error-message">
                {{ prefectureErrorMessage }}
              </div>
              <select
                id="delivery_address_form_prefecture_id"
                v-model="inputPrefecture"
                name="delivery_address_form[prefecture_id]"
                class="select-form"
                :class="{
                  'select-form--selected': isNotPrefectureEmpty,
                  'select-form--unselected': !isNotPrefectureEmpty,
                  field_with_errors: showablePrefectureErrorMessage
                }"
              >
                <option disabled value="">
                  都道府県
                </option>
                <option v-for="p in prefectures" :key="p.id" :value="`${p.id}`">
                  {{ p.name }}
                </option>
              </select>
            </div>
            <div class="form-input-group">
              <div v-if="showableAddress1ErrorMessage" class="error-message">
                {{ address1ErrorMessage }}
              </div>
              <input
                id="delivery_address_form_address1"
                v-model="inputAddress1"
                placeholder="市区町村"
                type="text"
                name="delivery_address_form[address1]"
                class="input-form"
                :class="{
                  field_with_errors: showableAddress1ErrorMessage,
                  'input-form--inputted': isNotAddress1Empty,
                  'input-form--inputting': isAddress1Focused
                }"
                @focus="onFocusAddress1"
                @blur="onBlurAddress1"
              />
            </div>
            <div class="form-input-group">
              <div v-if="showableAddress2ErrorMessage" class="error-message">
                {{ address2ErrorMessage }}
              </div>
              <input
                id="delivery_address_form_address2"
                ref="address2"
                v-model="inputAddress2"
                placeholder="番地、建物名、部屋番号など"
                type="text"
                name="delivery_address_form[address2]"
                class="input-form"
                :class="{
                  field_with_errors: showableAddress2ErrorMessage,
                  'input-form--inputted': isNotAddress2Empty,
                  'input-form--inputting': isAddress2Focused
                }"
                @focus="onFocusAddress2"
                @blur="onBlurAddress2"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TextWithLinks from '../../../../atoms/text/TextWithLinks.vue'

export default {
  components: {
    TextWithLinks
  },
  props: {
    prefectures: {
      type: Array,
      required: true
    },
    lastName: {
      type: String,
      required: true
    },
    firstName: {
      type: String,
      required: true
    },
    lastNameKana: {
      type: String,
      required: true
    },
    firstNameKana: {
      type: String,
      required: true
    },
    zipCode: {
      type: String,
      required: true
    },
    prefecture: {
      type: String,
      required: true
    },
    address1: {
      type: String,
      required: true
    },
    address2: {
      type: String,
      required: true
    },
    lastNameErrorMessage: {
      type: String,
      required: true
    },
    firstNameErrorMessage: {
      type: String,
      required: true
    },
    lastNameKanaErrorMessage: {
      type: String,
      required: true
    },
    firstNameKanaErrorMessage: {
      type: String,
      required: true
    },
    zipCodeErrorMessage: {
      type: String,
      required: true
    },
    prefectureErrorMessage: {
      type: String,
      required: true
    },
    address1ErrorMessage: {
      type: String,
      required: true
    },
    address2ErrorMessage: {
      type: String,
      required: true
    },
    ticketUsableLimitAt: {
      type: String,
      required: true
    },
    deliveryRestrictionText: {
      type: String,
      required: false,
      default: ''
    },
    itemDetailUrl: {
      type: String,
      required: false,
      default: ''
    }
  },
  data() {
    return {
      isLastNameFocused: false,
      isFirstNameFocused: false,
      isLastNameKanaFocused: false,
      isFirstNameKanaFocused: false,
      isZipCodeFocused: false,
      isAddress1Focused: false,
      isAddress2Focused: false
    }
  },
  computed: {
    inputLastName: {
      get() {
        return this.$props.lastName
      },
      set(value) {
        this.$emit('change-last-name', value)
      }
    },
    inputFirstName: {
      get() {
        return this.$props.firstName
      },
      set(value) {
        this.$emit('change-first-name', value)
      }
    },
    inputLastNameKana: {
      get() {
        return this.$props.lastNameKana
      },
      set(value) {
        this.$emit('change-last-name-kana', value)
      }
    },
    inputFirstNameKana: {
      get() {
        return this.$props.firstNameKana
      },
      set(value) {
        this.$emit('change-first-name-kana', value)
      }
    },
    inputZipCode: {
      get() {
        return this.$props.zipCode
      },
      set(value) {
        this.$emit('change-zip-code', value)
      }
    },
    inputPrefecture: {
      get() {
        return this.$props.prefecture
      },
      set(value) {
        this.$emit('change-prefecture', value)
      }
    },
    inputAddress1: {
      get() {
        return this.$props.address1
      },
      set(value) {
        this.$emit('change-address1', value)
      }
    },
    inputAddress2: {
      get() {
        return this.$props.address2
      },
      set(value) {
        this.$emit('change-address2', value)
      }
    },
    isNotLastNameEmpty() {
      return this.lastName.length !== 0
    },
    isNotFirstNameEmpty() {
      return this.firstName.length !== 0
    },
    isNotLastNameKanaEmpty() {
      return this.lastNameKana.length !== 0
    },
    isNotFirstNameKanaEmpty() {
      return this.firstNameKana.length !== 0
    },
    isNotZipCodeEmpty() {
      return this.zipCode.length !== 0
    },
    isNotAddress1Empty() {
      return this.address1.length !== 0
    },
    isNotAddress2Empty() {
      return this.address2.length !== 0
    },
    isNotLastNameErrorMessageEmpty() {
      return this.lastNameErrorMessage.length !== 0
    },
    isNotFirstNameErrorMessageEmpty() {
      return this.firstNameErrorMessage.length !== 0
    },
    isNotLastNameKanaErrorMessageEmpty() {
      return this.lastNameKanaErrorMessage.length !== 0
    },
    isNotFirstNameKanaErrorMessageEmpty() {
      return this.firstNameKanaErrorMessage.length !== 0
    },
    isNotPrefectureEmpty() {
      return this.prefecture !== ''
    },
    showableNameErrorMessage() {
      return (
        this.isNotLastNameErrorMessageEmpty ||
        this.isNotFirstNameErrorMessageEmpty
      )
    },
    showableNameKanaErrorMessage() {
      return (
        this.isNotLastNameKanaErrorMessageEmpty ||
        this.isNotFirstNameKanaErrorMessageEmpty
      )
    },
    showableZipCodeErrorMessage() {
      return this.zipCodeErrorMessage.length !== 0
    },
    showablePrefectureErrorMessage() {
      return this.prefectureErrorMessage.length !== 0
    },
    showableAddress1ErrorMessage() {
      return this.address1ErrorMessage.length !== 0
    },
    showableAddress2ErrorMessage() {
      return this.address2ErrorMessage.length !== 0
    },
    nameErrorMessage() {
      return this.lastNameErrorMessage + '\n' + this.firstNameErrorMessage
    },
    nameKanaErrorMessage() {
      return (
        this.lastNameKanaErrorMessage + '\n' + this.firstNameKanaErrorMessage
      )
    },
    hasDeliveryRestriction() {
      return (
        this.deliveryRestrictionText && this.deliveryRestrictionText.length > 0
      )
    }
  },
  methods: {
    onFocusLastName() {
      this.isLastNameFocused = true
    },
    onFocusFirstName() {
      this.isFirstNameFocused = true
    },
    onFocusLastNameKana() {
      this.isLastNameKanaFocused = true
    },
    onFocusFirstNameKana() {
      this.isFirstNameKanaFocused = true
    },
    onFocusZipCode() {
      this.isZipCodeFocused = true
    },
    onFocusAddress1() {
      this.isAddress1Focused = true
    },
    onFocusAddress2() {
      this.isAddress2Focused = true
    },
    onBlurLastName() {
      this.isLastNameFocused = false
    },
    onBlurFirstName() {
      this.isFirstNameFocused = false
    },
    onBlurLastNameKana() {
      this.isLastNameKanaFocused = false
    },
    onBlurFirstNameKana() {
      this.isFirstNameKanaFocused = false
    },
    onBlurZipCode() {
      this.isZipCodeFocused = false
    },
    onBlurAddress1() {
      this.isAddress1Focused = false
    },
    onBlurAddress2() {
      this.isAddress2Focused = false
    },
    focusAddress2() {
      this.$refs.address2.focus()
    }
  }
}
</script>
<style lang="scss" scoped>
.delivery-address {
  &__header-wrapper {
    width: 100%;
  }

  &__header {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 24px 16px;
    box-sizing: border-box;
  }

  &__header-title {
    display: flex;
    justify-content: left;
  }

  &__deadline-area {
    display: flex;
    padding: 8px 16px;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 4px;
    align-self: stretch;
    border-radius: 4px;
    background: $primary-lighten;
  }

  &__deadline-date {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__deadline-label {
    @extend %caption-bold;
    color: $brown-1;
  }

  &__deadline-value {
    @extend %body-bold;
    color: $primary;
  }

  &__deadline-notice {
    @extend %minimal;
    color: $brown-1;
    margin: 0;
  }

  &-attention {
    margin-bottom: 0;
  }

  &-restriction {
    margin-bottom: 0;

    &__content {
      display: flex;
      flex-direction: column;
      gap: calc(1rem * 1.6); // 1行分開ける

      p {
        margin: 0;
      }
    }

    &__title-detail {
      display: flex;
      flex-direction: column;
      gap: calc(1rem * 1.6); // 1行分開ける
    }

    &__title {
      font-weight: bold;
    }

    &__detail {
      white-space: pre-wrap;
    }

    &__link {
      color: $link;
    }
  }
}
</style>
